'use client';

import * as React from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { cn } from 'lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';

interface TimestampProps {
  /** Date, ISO string, or epoch ms. */
  date: Date | string | number;
  /** Override the inline label. Defaults to "X ago" / "in X". */
  children?: React.ReactNode;
  className?: string;
}

const toDate = (input: TimestampProps['date']): Date =>
  input instanceof Date ? input : new Date(input);

const Timestamp = React.forwardRef<HTMLSpanElement, TimestampProps>(
  ({ date, children, className }, ref) => {
    const d = toDate(date);
    const inline = children ?? format(d, 'MMM d, yyyy');
    const local = format(d, 'yyyy-MM-dd HH:mm:ss zzz');
    const utc = formatInTimeZone(d, 'UTC', "yyyy-MM-dd'T'HH:mm:ss'Z'");
    const relative = formatDistanceToNow(d, { addSuffix: true });

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            ref={ref}
            tabIndex={0}
            className={cn(
              'inline-flex cursor-help items-center border-b border-dotted border-[var(--line-strong)] pb-px font-mono text-xs text-[var(--text-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring-color)]',
              className,
            )}
          >
            {inline}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          // text-left forced because the trigger may sit inside a cell
          // with `text-align: center` (legacy table styling), and inline
          // alignment leaks into the tooltip via inheritance.
          className="grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-1 border border-[var(--rye-ink-3)] bg-[var(--rye-ink)] px-3.5 py-2.5 text-left font-mono text-[11px] text-[var(--rye-text-light)] shadow-[0_8px_24px_-10px_rgba(0,0,0,0.4)]"
        >
          <span className="self-center text-left text-[9px] uppercase tracking-[0.14em] text-white/45">
            Local
          </span>
          <span className="text-left">{local}</span>
          <span className="self-center text-left text-[9px] uppercase tracking-[0.14em] text-white/45">
            UTC
          </span>
          <span className="text-left">{utc}</span>
          <span className="self-center text-left text-[9px] uppercase tracking-[0.14em] text-white/45">
            Relative
          </span>
          <span className="text-left text-[var(--rye-lime)]">{relative}</span>
        </TooltipContent>
      </Tooltip>
    );
  },
);
Timestamp.displayName = 'Timestamp';

export { Timestamp };
